
import { SitesMultipleClusterIcon } from '@tableau/icons/SitesMultipleClusterIcon';
import { MenuItemInfo, Tooltip } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';

import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';

import { AlertDetailsPopover } from '../dataConnectAlerts/AlertDetailsPopover';
import { ClusterNodePresenceIcon } from '../dataConnectClusterNodes/ClusterNodePresenceIcon';
import { getClusterNodeStatus } from '../dataConnectClusterNodes/ClusterNodeStates';
import { ActionItemInfo, ActionItemType, ActionType, MoreActionsMenu } from '../MoreActionsMenu';
import { Cluster } from '../types/Cluster';
import { Node } from '../types/Node';
import { NodeResUsage } from '../types/NodeResUsage';
import { AddNodeButton } from './AddNodeButton';
import { ClusterNamePopover } from './ClusterNamePopover';
import { textCellPadding } from './DataConnectMultiClusterTableHeaders';
import { openDeleteClusterDialog } from './DeleteClusterDialog';
import { openEditClusterDialog } from './EditClusterDialog';
import { openGetDockerRegistryDialog } from './GetDockerRegistryAction';
import { openShareClusterDialog } from './ShareClusterAction';


export type MultiClusterTableEntryClusterProps = Readonly<{
  cluster: Cluster;
  existingClusterNames: ReadonlyArray<string>;
  isExpanded: boolean;
  allowEditDisplayName: boolean;
  allowGetContainerRegistry: boolean;
  allowDeleteCluster: boolean;
  allowShareCluster: boolean;
  refreshPoolsAndClusters: () => void;
}>;

export function DataConnectMultiClusterTableEntryCluster(props: MultiClusterTableEntryClusterProps) {
  const { cluster, existingClusterNames, isExpanded, refreshPoolsAndClusters } = props;
  const nodes: Node[] = Object.values(cluster.nodes);
  let clusterCellStyle =  textCellPadding + ' tb-data-grid-separator-row ';

  clusterCellStyle += isExpanded ? expandedRowFocus : '';

  const targetSelectItem: MenuItemInfo[] = [
    { displayValue: Messages.dataConnectSiteSettingsShareClusterButton(), value: ActionType.SHARE_CLUSTER, disabled: !props.allowShareCluster },
    { displayValue: Messages.dataConnectSiteSettingsEditClusterNameButton(), value: ActionType.EDIT_CLUSTER_NAME, disabled: !props.allowEditDisplayName },
    { displayValue: Messages.dataConnectSiteSettingsGetDockerRegistryButton(), value: ActionType.GET_DOCKER_REGISTRY, disabled: !props.allowGetContainerRegistry},
    { displayValue: Messages.deleteMessage(), value: ActionType.DELETE, disabled: !props.allowDeleteCluster},
  ];
  const clusterNodeStatus = getClusterNodeStatus();
  return <>
    <td className={clusterCellStyle} style={{paddingLeft:0}}>
      <ClusterNamePopover cluster={cluster}/>
      <span style={{paddingLeft: 4, verticalAlign: 'text-top'}}>
        { cluster.shared && <>
          <Tooltip children={<SitesMultipleClusterIcon size={12} alt={''} />}
               tooltipContent={Messages.dataConnectSiteSettingsClusterShared()}
               position={Tooltip.defaultProps.position!}
               afterDelay={false}
               followCursor={false} />
          </>}
      </span>
    </td>
    <td className={clusterCellStyle}>
      <span className={'tb-data-grid-cell-text-data-connect tb-lr-padded-wide'} data-tb-test-id={'cluster-status-cell'}>
        {<ClusterNodePresenceIcon status={cluster.status} clusterNodeStatus={clusterNodeStatus} />}
        {clusterNodeStatus[cluster.status] === undefined ? clusterNodeStatus.Available.stateLabel : clusterNodeStatus[cluster.status].stateLabel}
      </span>
    </td>
    <td className={clusterCellStyle}>
      <AlertDetailsPopover alerts={cluster.alerts}/>
    </td>
    <td className={clusterCellStyle} style={{textAlign: 'right'}}>
      <span className={'tb-data-grid-cell-text-data-connect tb-lr-padded-wide'} data-tb-test-id={'cluster-cpu-cell'}>
        {nodes.length === 0 && <span>0%</span>}
        {nodes.length !== 0 && <span>{cacualteCPUUtilization(Object.values(cluster.nodesResUsage))}</span>}
      </span>
    </td>
    <td className={clusterCellStyle} style={{textAlign: 'right'}}>
      <span className={'tb-data-grid-cell-text-data-connect tb-lr-padded-wide'} data-tb-test-id={'cluster-memory-cell'}>
        {nodes.length === 0 && <span>0%</span>}
        {nodes.length !== 0 && <span>{cacualteMemoryUtilization(Object.values(cluster.nodesResUsage))}</span>}
      </span>
    </td>
    <td className={clusterCellStyle}>
      <span className={'tb-data-grid-cell-text-data-connect tb-lr-padded-wide'} data-tb-test-id={'cluster-actions-cell'}>
        <AddNodeButton cluster={cluster} />
        <MoreActionsMenu
          targetItemInfo={targetSelectItem}
          actionType= {ActionItemType.NODE_ACTION}
          actionItemInfo={{ clusterName: cluster.displayName,
                        clusterId: cluster.clusterId,
                        existingClusterNames: existingClusterNames,
                        refreshPoolsAndClusters: refreshPoolsAndClusters}}
          onHandleMenuSelect={onHandleMenuSelect}
        />
       </span>
    </td>
  </>;
}

const onHandleMenuSelect = (actionItem: ActionItemInfo, actionType: string) => {
  if (actionType === ActionType.DELETE) {
    openDeleteClusterDialog(actionItem, actionItem.refreshPoolsAndClusters);
  } else if (actionType === ActionType.EDIT_CLUSTER_NAME) {
    openEditClusterDialog(actionItem, actionItem.refreshPoolsAndClusters);
  } else if (actionType === ActionType.GET_DOCKER_REGISTRY) {
    openGetDockerRegistryDialog(actionItem);
  } else if (actionType === ActionType.SHARE_CLUSTER) {
    openShareClusterDialog(actionItem);
  }
};

const cacualteCPUUtilization = (usages: NodeResUsage[]) => {
  if (usages.length === 0) {
    return  '0% (0 Core)';
  }
  var totalCpuAllocated = 0;
  var totalCpuCapacity = 0;
  usages.forEach(item => {
    totalCpuAllocated += item.cpuAllocated;
    totalCpuCapacity += item.cpuCapacity;
  });
  if (totalCpuCapacity === 0) {
    return  '0% (0 Core)';
  }
  return ((totalCpuAllocated/totalCpuCapacity) * 100).toFixed(2)+'% ('+(totalCpuAllocated/1000).toFixed(2)+' Cores)';
};

const cacualteMemoryUtilization = (usages: NodeResUsage[]) => {
  if (usages.length === 0) {
    return '0% (0 GB)';
  }
  var totalMemAllockated = 0;
  var totalMemCapacity = 0;
  usages.forEach(item => {
    totalMemAllockated += item.memoryAllocatedKb;
    totalMemCapacity += item.memoryCapacityKb;
  });
  if (totalMemCapacity === 0) {
    return '0% (0 GB)';
  }
  return ((totalMemAllockated/totalMemCapacity) * 100).toFixed(2)+'% ('+(totalMemAllockated/1024/1024/1024).toFixed(2)+' GB)';
};

export const expandedRowFocus = style({
    backgroundColor: CommonStyles.Colors.BlueHighlight
  });
